import { Component, type ReactNode } from 'react'
import { RouterProvider } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { router } from '@/routes'
import queryClient from '@/services/query/queryClient'
import './index.css'

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class AppErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[AppErrorBoundary] Uncaught error:', error, info)
  }

  render() {
    if (this.state.hasError) {
      const isFirebaseError = this.state.error?.message?.includes('Firebase') ||
        this.state.error?.message?.includes('api-key') ||
        this.state.error?.message?.includes('auth/invalid')

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={32} className="text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Something went wrong</h1>
                <p className="text-gray-500 text-sm mt-2">
                  {isFirebaseError
                    ? 'Firebase configuration error. Please check your .env file and ensure valid Firebase credentials are set.'
                    : 'An unexpected error occurred. Please try refreshing the page.'}
                </p>
              </div>
              {this.state.error && (
                <details className="w-full text-left">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                    Technical details
                  </summary>
                  <pre className="mt-2 text-xs bg-gray-50 border rounded p-3 overflow-auto text-red-600 whitespace-pre-wrap">
                    {this.state.error.message}
                  </pre>
                </details>
              )}
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <RefreshCw size={16} />
                Reload App
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </AppErrorBoundary>
  )
}

export default App
